/**
 * music-sequencer/midi-io.js — MIDI ⇄ проект /music-sequencer.
 *
 * ЗАЧЕМ. Экспорт MIDI у секвенсора был, импорта — нет: MIDI, выбранный через
 * «Загрузить аудио», получал отказ «это ноты, а не звук» (7 сессий за 90 дней
 * до 25.09.2026, и отказ был честным ответом на вопрос, который никто не
 * задавал: человек принёс бит, а не звук). При этом MIDI с ударными выдают
 * наши же /drum-separator, /virtual-drums и /midi-humanizer — секвенсор был
 * тупиком для их результата.
 *
 * ОДИН ВЛАДЕЛЕЦ соответствия «инструмент ⇄ нота General MIDI» в обе стороны:
 * экспорт пишет ноты GM_DRUM, импорт читает всю перкуссионную раскладку GM
 * (27–87) в 18 звуков секвенсора — сюда, а не в блейд, иначе экспорт и импорт
 * разойдутся на первой же правке.
 *
 * Ноты файла читает общая дверь MidiStreamNotes.read (midi-stream-notes.js):
 * удар и отпускание спариваются по порядку в потоке, как в секвенсоре, а не
 * как у @tonejs/midi. Модуль получает её результат и ничего не парсит сам.
 *
 * ИМПОРТ — что теряется и что человек об этом узнаёт (stats):
 *   • шаг — шестнадцатая; удар ставится на ближайшую (со свингом — на
 *     ближайшую СО СДВИГОМ, свинг узнаётся по файлу и ставится ползунку);
 *   • в сетке 18 звуков, в GM 61 — родственные сливаются (все томы → tom);
 *   • мелодия секвенсора — одна нота на шаг из белых клавиш C3–B4: берётся
 *     верхний голос, октава сворачивается в диапазон, диез уходит на белую
 *     клавишу ниже (stats.melodyMoved — сколько нот сдвинуто);
 *   • паттернов 8, в паттерне до 32 шагов: файл, который повторяет один и тот
 *     же кусок, приходит этим куском (stats.period), длинный — первыми
 *     тактами, которые влезли (stats.truncatedBars);
 *   • темп один: первый темп файла (stats.tempoChanges — сколько было ещё).
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    else if (root) root.MSMidi = api;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var TPQ = 96;
    var DRUM_CH = 9;
    var LEVELS = [0.25, 0.5, 0.75, 1];

    /* Инструмент → нота GM (экспорт). */
    var GM_DRUM = { kick: 36, snare: 38, clap: 39, hihat: 42, tom: 45, cowbell: 56, shaker: 70, cymbal: 49,
        woodblock: 76, conga: 63, openhat: 46, tambourine: 54, bongo: 61, rimshot: 37, triangle: 81,
        claves: 75, agogo: 67, whistle: 72 };

    /* Нота GM 27–87 → инструмент (импорт). Своя нота каждого инструмента
       возвращается в него же — круг «экспорт → импорт» замкнут. */
    var GM_TO_INST = (function () {
        var m = {}, groups = {
            kick: [35, 36], rimshot: [37], snare: [38, 40], clap: [39, 28],
            tom: [41, 43, 45, 47, 48, 50, 65, 66, 86, 87], hihat: [42, 44], openhat: [46],
            cymbal: [49, 51, 52, 53, 55, 57, 59], cowbell: [56, 34], tambourine: [54, 83],
            shaker: [69, 70, 82, 58, 73, 74, 29, 30], bongo: [60, 61], conga: [62, 63, 64, 78, 79],
            agogo: [67, 68], whistle: [71, 72], claves: [75, 31, 85, 27], woodblock: [76, 77, 33, 32],
            triangle: [80, 81, 84]
        };
        Object.keys(groups).forEach(function (k) { groups[k].forEach(function (n) { m[n] = k; }); });
        return m;
    })();

    function velLevel(v) {
        var x = v / 127, best = LEVELS[0];
        for (var i = 1; i < LEVELS.length; i++) if (Math.abs(LEVELS[i] - x) < Math.abs(best - x)) best = LEVELS[i];
        return best;
    }

    /* ── Экспорт ─────────────────────────────────────────────────────────── */

    function varLen(out, v) {
        var buf = [v & 0x7f];
        while ((v >>= 7) > 0) buf.unshift((v & 0x7f) | 0x80);
        for (var i = 0; i < buf.length; i++) out.push(buf[i]);
    }

    /**
     * Стандартный MIDI-файл формата 0.
     * @param {{bpm:number, events:Array<{tick:number,ch:number,note:number,vel:number,dur:number}>, tpq?:number}} o
     * @returns {Uint8Array}
     */
    function encode(o) {
        var tpq = o.tpq || TPQ, track = [], uspq = Math.round(60000000 / o.bpm);
        track.push(0x00, 0xFF, 0x51, 0x03, (uspq >> 16) & 0xFF, (uspq >> 8) & 0xFF, uspq & 0xFF);
        /* Удар и отпускание — отдельными событиями по времени; отпускание в
           том же тике идёт раньше удара (запись «удар, длительность, отпускание»
           сдвигала каждую следующую ноту на длительность предыдущей). */
        var evs = [];
        o.events.forEach(function (e) {
            evs.push({ tick: e.tick, on: 1, ch: e.ch, note: e.note, vel: e.vel });
            evs.push({ tick: e.tick + e.dur, on: 0, ch: e.ch, note: e.note, vel: 0 });
        });
        evs.sort(function (a, b) { return a.tick - b.tick || a.on - b.on || a.ch - b.ch; });
        var prev = 0;
        evs.forEach(function (e) {
            varLen(track, e.tick - prev);
            track.push((e.on ? 0x90 : 0x80) | e.ch, e.note & 0x7f, e.vel & 0x7f);
            prev = e.tick;
        });
        varLen(track, 0);
        track.push(0xFF, 0x2F, 0x00);
        var head = [0x4D, 0x54, 0x68, 0x64, 0, 0, 0, 6, 0, 0, 0, 1, (tpq >> 8) & 0xFF, tpq & 0xFF];
        var th = [0x4D, 0x54, 0x72, 0x6B, (track.length >>> 24) & 0xFF, (track.length >> 16) & 0xFF, (track.length >> 8) & 0xFF, track.length & 0xFF];
        var out = new Uint8Array(head.length + th.length + track.length);
        out.set(head, 0); out.set(th, head.length); out.set(track, head.length + th.length);
        return out;
    }

    /* ── Импорт ──────────────────────────────────────────────────────────── */

    function median(a) {
        if (!a.length) return 0;
        var s = a.slice().sort(function (x, y) { return x - y; });
        return s[s.length >> 1];
    }

    /**
     * Свинг файла: насколько нечётные шестнадцатые стоят позже чётных, в долях
     * шестнадцатой. 0 — ровно. Та же шкала, что у ползунка: 100 % = 1/3.
     */
    function detectSwing(pos) {
        var odd = [], even = [];
        for (var i = 0; i < pos.length; i++) {
            var n = Math.round(pos[i]);
            (n & 1 ? odd : even).push(pos[i] - n);
        }
        /* Со свингом нечётная шестнадцатая уезжает на +k (k ≤ 1/3), то есть
           ближайший целый шаг у неё тот же — округление не сломано.
           Порог 0.07 шестнадцатой (≈21 % ползунка): на корпусе Lakh при 0.04
           «свинг» 12–13 % находился у прямых песен, сыгранных живьём
           (Billie Jean, Surfin USA) — это разброс игры, а не приём. */
        if (odd.length < 4) return 0;
        var mo = median(odd), k = mo - (even.length ? median(even) : 0);
        if (!(k >= 0.07)) return 0;
        var near = 0;
        for (i = 0; i < odd.length; i++) if (Math.abs(odd[i] - mo) < 0.08) near++;
        if (near < odd.length * 0.6) return 0;
        return Math.max(0, Math.min(100, Math.round(k / (1 / 3) * 100)));
    }

    function meterSteps(ts, allowed) {
        if (!ts || !(ts.num > 0) || !(ts.den > 0)) return { steps: 16, supported: true };
        var s = ts.num * 16 / ts.den;
        return (s === Math.round(s) && allowed.indexOf(s) !== -1) ? { steps: s, supported: true } : { steps: 16, supported: false };
    }

    /**
     * Результат MidiStreamNotes.read → проект секвенсора.
     * @param {object|null} parsed
     * @param {{instruments:string[], melodyMidi:number[], maxPatterns?:number, stepsAllowed?:number[]}} opts
     *   instruments — id инструментов в порядке сетки ('melody' — мелодия);
     *   melodyMidi — MIDI-номер каждой строки мелодии (индекс = значение melodyGrid).
     * @returns {{error:string}|{bpm:number, swing:number, steps:number, active:string[],
     *   patterns:Array<{grid:object, melodyGrid:number[], melodyVelocity:number[], steps:number}>,
     *   arrangement:number[], stats:object}}
     */
    function toProject(parsed, opts) {
        if (!parsed || !parsed.tracks) return { error: 'unreadable' };
        if (!parsed.ppq) return { error: 'smpte' };
        var maxPat = opts.maxPatterns || 8, allowed = opts.stepsAllowed || [8, 12, 16, 24, 32];
        var tps = parsed.ppq / 4, stats = { drumHits: 0, melodyNotes: 0, unmappedDrums: 0, melodyMoved: 0, polyphonyDropped: 0 };

        var notes = [];
        parsed.tracks.forEach(function (t) { (t.notes || []).forEach(function (n) { notes.push(n); }); });
        var drums = [], mel = [];
        notes.forEach(function (n) {
            if (n.channel === DRUM_CH) {
                var inst = GM_TO_INST[n.midi];
                if (inst && opts.instruments.indexOf(inst) !== -1) drums.push(n); else stats.unmappedDrums++;
            } else mel.push(n);
        });
        if (opts.instruments.indexOf('melody') === -1) mel = [];
        if (!drums.length && !mel.length) return { error: 'empty' };

        /* Темп: первый в файле (секвенсор держит один), в пределах ползунка. */
        var tempos = parsed.tempos || [];
        var bpmRaw = tempos.length ? 60000000 / tempos[0].usPerQuarter : 120;
        var bpm = Math.max(40, Math.min(200, Math.round(bpmRaw)));
        stats.tempoChanges = Math.max(0, tempos.filter(function (x, i) { return i === 0 || x.usPerQuarter !== tempos[i - 1].usPerQuarter; }).length - 1);
        stats.tempoClamped = bpm !== Math.round(bpmRaw);

        var ts = (parsed.timeSignatures || [])[0] || null;
        var meter = meterSteps(ts, allowed), barSteps = meter.steps;
        stats.meter = ts ? ts.num + '/' + ts.den : '4/4';
        stats.meterSupported = meter.supported;

        var pos = drums.concat(mel).map(function (n) { return n.ticks / tps; });
        var swing = detectSwing(pos), k = swing / 100 / 3;
        function stepOf(p) {
            var f = Math.floor(p), best = f, bd = Infinity;
            for (var c = f - 1; c <= f + 2; c++) {
                var d = Math.abs(p - (c + (c & 1 ? k : 0)));
                if (d < bd) { bd = d; best = c; }
            }
            return Math.max(0, best);
        }

        /* Шаг каждой ноты — рядом с нотой, не в ней: объекты принадлежат
           результату общей двери, и мутировать их — чужая забота. */
        var first = Infinity, last = 0;
        var D = drums.map(function (n) { return { n: n, s: stepOf(n.ticks / tps) }; });
        var Mn = mel.map(function (n) { return { n: n, s: stepOf(n.ticks / tps) }; });
        D.concat(Mn).forEach(function (x) { if (x.s < first) first = x.s; if (x.s > last) last = x.s; });
        /* Пустые такты в начале (отсчёт, пауза до вступления) не переносим. */
        var bar0 = Math.floor(first / barSteps), nBars = Math.floor(last / barSteps) - bar0 + 1;

        /* Такты: ударные (громкость на шаг) + мелодия (верхний голос). */
        var bars = [];
        for (var b = 0; b < nBars; b++) bars.push({ d: {}, m: [], mv: [] });
        D.forEach(function (x) {
            var s = x.s - bar0 * barSteps, bar = bars[Math.floor(s / barSteps)], i = s % barSteps, inst = GM_TO_INST[x.n.midi];
            var row = bar.d[inst] || (bar.d[inst] = []);
            var v = velLevel(x.n.velocity);
            if (!(row[i] >= v)) row[i] = v;
            stats.drumHits++;
        });
        var table = opts.melodyMidi, lo = Math.min.apply(null, table), hi = Math.max.apply(null, table);
        function melodyIndex(m) {
            var x = m;
            while (x < lo) x += 12;
            while (x > hi) x -= 12;
            var i = table.indexOf(x);
            if (i < 0) i = table.indexOf(x - 1);
            if (i < 0) i = table.indexOf(x + 1);
            return { i: i, moved: x !== m || table[i] !== x };
        }
        var topAt = {};
        Mn.forEach(function (x) {
            var t = topAt[x.s];
            if (!t || x.n.midi > t.n.midi) { if (t) stats.polyphonyDropped++; topAt[x.s] = x; }
            else stats.polyphonyDropped++;
        });
        Object.keys(topAt).forEach(function (key) {
            var x = topAt[key], s = x.s - bar0 * barSteps, bar = bars[Math.floor(s / barSteps)], i = s % barSteps;
            var r = melodyIndex(x.n.midi);
            if (r.i < 0) return;
            bar.m[i] = r.i;
            bar.mv[i] = velLevel(x.n.velocity);
            stats.melodyNotes++;
            if (r.moved) stats.melodyMoved++;
        });

        /* Файл-петля приходит своей петлёй: наименьший период тактов, которым
           повторяется весь файл. */
        var sig = bars.map(function (x) { return JSON.stringify([x.d, x.m, x.mv]); });
        var period = nBars;
        for (var p = 1; p < nBars; p++) {
            var ok = true;
            for (var q = p; q < nBars && ok; q++) if (sig[q] !== sig[q % p]) ok = false;
            if (ok) { period = p; break; }
        }
        stats.bars = nBars; stats.period = period;

        /* Длина паттерна — наименьшая допустимая кратная такту, при которой
           период помещается в maxPat паттернов; не поместился — сколько влезет. */
        var mult = allowed.filter(function (s) { return s % barSteps === 0; }).sort(function (x, y) { return x - y; });
        var L = mult[mult.length - 1];
        for (var mi = 0; mi < mult.length; mi++) {
            if (Math.ceil(period / (mult[mi] / barSteps)) <= maxPat) { L = mult[mi]; break; }
        }
        var perPat = L / barSteps, keep = Math.min(period, maxPat * perPat);
        stats.barsKept = keep; stats.truncatedBars = period - keep;

        var instUsed = {};
        bars.slice(0, keep).forEach(function (x) { Object.keys(x.d).forEach(function (id) { instUsed[id] = 1; }); });
        var hasMel = stats.melodyNotes > 0;
        var active = opts.instruments.filter(function (id) { return id === 'melody' ? hasMel : !!instUsed[id]; });

        var patterns = [];
        for (var pi = 0; pi * perPat < keep; pi++) {
            var pat = { grid: {}, melodyGrid: new Array(L).fill(-1), melodyVelocity: new Array(L).fill(1), steps: L };
            active.forEach(function (id) { if (id !== 'melody') pat.grid[id] = new Array(L).fill(0); });
            for (var j = 0; j < perPat && pi * perPat + j < keep; j++) {
                var src = bars[pi * perPat + j], off = j * barSteps;
                Object.keys(src.d).forEach(function (id) {
                    src.d[id].forEach(function (v, i) { if (v) pat.grid[id][off + i] = v; });
                });
                src.m.forEach(function (ix, i) { if (ix != null) { pat.melodyGrid[off + i] = ix; pat.melodyVelocity[off + i] = src.mv[i]; } });
            }
            patterns.push(pat);
        }
        var arrangement = patterns.map(function (_, i) { return i; });
        stats.swing = swing;
        return { bpm: bpm, swing: swing, steps: L, active: active, patterns: patterns, arrangement: arrangement, stats: stats };
    }

    return { TPQ: TPQ, GM_DRUM: GM_DRUM, GM_TO_INST: GM_TO_INST, velLevel: velLevel, encode: encode,
        detectSwing: detectSwing, toProject: toProject };
});
